import { api, describeError } from '@/services/api'

/**
 * Browser-side push subscription.
 *
 * The important distinction here is between the browser having a subscription
 * and the *server* knowing about it. Those can disagree — the browser keeps
 * its subscription across reinstalls and key changes, while the server row can
 * be missing, stale, or bound to different VAPID keys. Treating the browser's
 * view as the truth produced a toggle that read "on" while nothing could
 * actually be delivered, so both are checked and reconciled.
 */
export interface PushState {
  supported: boolean
  permission: NotificationPermission | 'unsupported'
  /** This browser holds a subscription object. */
  browserSubscribed: boolean
  /** The server has a row for *this* browser's endpoint. */
  registered: boolean
  /** Devices registered to this account in total. */
  devices: number
  /** False until an admin has generated the VAPID keys. */
  configured: boolean
  /** The browser's subscription was made with the server's current key. */
  keyMatches: boolean
}

const EMPTY: PushState = {
  supported: false,
  permission: 'unsupported',
  browserSubscribed: false,
  registered: false,
  devices: 0,
  configured: false,
  keyMatches: false,
}

/**
 * The applicationServerKey must be raw bytes, not the base64url string.
 *
 * Backed by an explicit ArrayBuffer so the type is `Uint8Array<ArrayBuffer>`:
 * `subscribe()` will not accept one that might be backed by a
 * SharedArrayBuffer.
 */
function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4)
  const normalised = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = window.atob(normalised)
  const output = new Uint8Array(new ArrayBuffer(raw.length))
  for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i)
  return output
}

function bufferToBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte)
  })
  return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

import { pushNeedsInstallFirst } from './install'

export function pushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  )
}

/**
 * `serviceWorker.ready` never rejects — if no worker ever activates it simply
 * hangs, which surfaces as a button that does nothing at all. Bound it.
 */
async function readyRegistration(timeoutMs = 8000): Promise<ServiceWorkerRegistration> {
  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise<never>((_, reject) =>
      window.setTimeout(
        () => reject(new Error('The service worker did not become ready. Reload the page and try again.')),
        timeoutMs,
      ),
    ),
  ])
}

async function currentSubscription(): Promise<PushSubscription | null> {
  try {
    const registration = await readyRegistration()
    return await registration.pushManager.getSubscription()
  } catch {
    return null
  }
}

export async function getPushState(): Promise<PushState> {
  if (!pushSupported()) return EMPTY

  const subscription = await currentSubscription()

  let configured = false
  let registered = false
  let devices = 0
  let serverKey: string | null = null

  try {
    // POST with the endpoint in the body: a push endpoint is a full URL, and
    // this host's WAF rejects a query string containing one with a 406.
    const status = await api.post<{
      enabled: boolean
      devices: number
      registered: boolean
      publicKey: string | null
    }>('/push/status', subscription ? { endpoint: subscription.endpoint } : {})
    configured = status.enabled
    registered = status.registered
    devices = status.devices
    serverKey = status.publicKey
  } catch {
    /*
     * Could not ask the server. Report what the browser knows and leave
     * `registered` false, but note that this is "unknown" rather than
     * "definitely not" — callers should not tear down a working subscription
     * on the strength of it.
     */
  }

  // A subscription made against different VAPID keys can never be delivered to.
  let keyMatches = false
  if (subscription && serverKey) {
    const applicationKey = subscription.options?.applicationServerKey
    keyMatches = applicationKey ? bufferToBase64Url(applicationKey) === serverKey : false
  }

  return {
    supported: true,
    permission: Notification.permission,
    browserSubscribed: subscription !== null,
    registered,
    devices,
    configured,
    keyMatches,
  }
}

/** Sends the current subscription to the server. */
async function registerWithServer(subscription: PushSubscription): Promise<void> {
  const raw = subscription.toJSON() as { endpoint?: string; keys?: Record<string, string> }
  await api.post('/push/subscribe', { endpoint: raw.endpoint, keys: raw.keys })
}

/**
 * Asks permission, subscribes, and registers the endpoint with the server.
 * @returns null on success, or a message explaining why it did not work.
 */
export async function enablePush(): Promise<string | null> {
  if (!pushSupported()) {
    /*
     * On iOS the Push API simply is not there in a Safari tab — it appears
     * only once the app is on the home screen. "This browser cannot" is both
     * untrue and a dead end there, so say the thing that actually helps.
     */
    return pushNeedsInstallFirst()
      ? 'On iPhone and iPad, notifications only work once the app is on your home screen. Add it there first, then open it from the home screen and turn these on.'
      : 'This browser cannot show push notifications.'
  }

  let publicKey: string | null = null
  try {
    const config = await api.get<{ enabled: boolean; publicKey: string | null }>('/push/config')
    if (!config.enabled || !config.publicKey) {
      return 'Push is not set up on the server yet — an administrator needs to generate the keys in Admin → Notifications.'
    }
    publicKey = config.publicKey
  } catch (configError) {
    return describeError(configError)
  }

  const permission = await Notification.requestPermission()
  if (permission !== 'granted') {
    return permission === 'denied'
      ? 'Notifications are blocked for this site. Allow them in your browser settings, then try again.'
      : 'Notifications were not enabled.'
  }

  try {
    const registration = await readyRegistration()
    let subscription = await registration.pushManager.getSubscription()

    // An existing subscription bound to different keys is undeliverable, and
    // subscribe() refuses to replace it — it has to go first.
    if (subscription) {
      const applicationKey = subscription.options?.applicationServerKey
      const matches = applicationKey ? bufferToBase64Url(applicationKey) === publicKey : false
      if (!matches) {
        await subscription.unsubscribe()
        subscription = null
      }
    }

    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        // Required by Chrome: every push must result in a visible notification.
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      })
    }

    await registerWithServer(subscription)
    return null
  } catch (subscribeError) {
    return describeError(subscribeError)
  }
}

/** Unsubscribes this device and forgets it server-side. */
export async function disablePush(): Promise<string | null> {
  if (!pushSupported()) return null
  try {
    const subscription = await currentSubscription()
    if (!subscription) return null

    const endpoint = subscription.endpoint
    await subscription.unsubscribe()
    await api.post('/push/unsubscribe', { endpoint })
    return null
  } catch (unsubscribeError) {
    return describeError(unsubscribeError)
  }
}

/**
 * Reconciles browser and server, quietly, at startup.
 *
 * Covers the case that caused "no devices are registered" while the toggle
 * read on: the browser kept a subscription the server had never been told
 * about, so nothing re-registered it and nothing reported the mismatch.
 */
export async function syncPushSubscription(): Promise<void> {
  if (!pushSupported() || Notification.permission !== 'granted') return

  try {
    const state = await getPushState()
    if (!state.configured || !state.browserSubscribed) return
    if (state.registered && state.keyMatches) return

    const registration = await readyRegistration()
    let subscription = await registration.pushManager.getSubscription()
    if (!subscription) return

    if (!state.keyMatches) {
      // Bound to superseded keys — replace it entirely.
      const config = await api.get<{ publicKey: string | null }>('/push/config')
      if (!config.publicKey) return
      await subscription.unsubscribe()
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(config.publicKey),
      })
    }

    await registerWithServer(subscription)
  } catch {
    // Best effort: the account menu still offers to switch it on by hand.
  }
}

export interface PushDiagnostic {
  step: string
  ok: boolean
  detail: string
}

/**
 * Walks the whole chain and reports each step.
 *
 * Every stage is caught individually: the point is to find where it stops,
 * which a single try/catch around the lot cannot tell you.
 */
export async function diagnosePush(): Promise<PushDiagnostic[]> {
  const report: PushDiagnostic[] = []
  const add = (step: string, ok: boolean, detail: string) => report.push({ step, ok, detail })

  add('Secure context', window.isSecureContext, window.isSecureContext ? 'https' : 'Push requires HTTPS')
  add('Service worker API', 'serviceWorker' in navigator, 'serviceWorker' in navigator ? 'present' : 'missing')
  add('Push API', 'PushManager' in window, 'PushManager' in window ? 'present' : 'missing')
  add('Notification API', 'Notification' in window, 'Notification' in window ? Notification.permission : 'missing')

  if (!pushSupported()) return report

  let registration: ServiceWorkerRegistration | null = null
  try {
    registration = await readyRegistration(8000)
    add('Service worker ready', true, `scope ${registration.scope}`)
    add(
      'Worker controlling this page',
      navigator.serviceWorker.controller !== null,
      navigator.serviceWorker.controller
        ? 'yes'
        : 'no — the page loaded before the worker took over. Reload once.',
    )
  } catch (error) {
    add('Service worker ready', false, error instanceof Error ? error.message : String(error))
    return report
  }

  let publicKey: string | null = null
  try {
    const config = await api.get<{ enabled: boolean; publicKey: string | null }>('/push/config')
    publicKey = config.publicKey
    add('Server VAPID keys', config.enabled, config.enabled ? 'configured' : 'not generated yet')
  } catch (error) {
    add('Server VAPID keys', false, describeError(error))
    return report
  }

  let subscription: PushSubscription | null = null
  try {
    subscription = await registration.pushManager.getSubscription()
    add(
      'Browser subscription',
      subscription !== null,
      subscription ? `${subscription.endpoint.slice(0, 48)}…` : 'none yet',
    )
  } catch (error) {
    add('Browser subscription', false, describeError(error))
  }

  if (subscription && publicKey) {
    const applicationKey = subscription.options?.applicationServerKey
    const matches = applicationKey ? bufferToBase64Url(applicationKey) === publicKey : false
    add('Subscription matches server keys', matches, matches ? 'yes' : 'no — it must be recreated')
  }

  if (!subscription && publicKey) {
    try {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      })
      add('Subscribe', true, 'created a new subscription')
    } catch (error) {
      add('Subscribe', false, error instanceof Error ? `${error.name}: ${error.message}` : String(error))
      return report
    }
  }

  if (subscription) {
    try {
      await registerWithServer(subscription)
      add('Register with server', true, 'accepted')
    } catch (error) {
      add('Register with server', false, describeError(error))
      return report
    }

    try {
      const status = await api.post<{ registered: boolean; devices: number }>('/push/status', {
        endpoint: subscription.endpoint,
      })
      add(
        'Server confirms this device',
        status.registered,
        status.registered ? `${status.devices} device(s) on this account` : 'the row was not saved',
      )
    } catch (error) {
      add('Server confirms this device', false, describeError(error))
    }
  }

  return report
}
